import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

interface Candidate {
  id: number;
  name: string;
  position: string;
  image: string;
  description: string;
}

const HomePage = () => {
  const [hoveredCandidate, setHoveredCandidate] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'AfD Bad Windsheim - Kommunalwahl 2026',
    description: 'AfD Bad Windsheim - Gemeinsam für eine starke Zukunft. Informationen zur Kommunalwahl 2026, unsere Kandidaten für Stadtrat und Kreistag sowie unser Programm für Bad Windsheim.',
    keywords: 'AfD Bad Windsheim, Kommunalwahl 2026, Stadtrat Bad Windsheim, Kreistag, Politik Bad Windsheim',
    canonical: `${siteUrl}/`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'PoliticalParty',
      name: 'AfD Bad Windsheim',
      url: siteUrl,
      logo: 'https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/AfD+BW+Logo+blau-1920w.jpg',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Augustinumstraße 4',
        addressLocality: 'Bad Windsheim',
        postalCode: '91438',
        addressCountry: 'DE'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+49-155-68844604',
        contactType: 'customer service',
        email: 'benno.bulitta@web.de'
      },
      sameAs: [
        'https://www.facebook.com/p/AfD-Ortsverband-Bad-Windsheim-61574808512040/',
        'https://www.instagram.com/afd_ov_bw/'
      ]
    }
  });

  const stadtratCandidates: Candidate[] = [
    {
      id: 1,
      name: "Dennis Neumeister",
      position: "Bauzeichner, 26 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/Dennis+Neumeister-1920w.jpg",
      description: "Ich bin 26 Jahre alt, gelernter Bauzeichner und studiere derzeit Bauingenieurwesen neben meiner Tätigkeit in einem Planungsbüro. Meine Zeit im Internat sowie der freiwillige Wehrdienst bei der Bundeswehr haben mich geprägt und mir früh vermittelt, wie wichtig Teamfähigkeit, Disziplin und Verlässlichkeit sind. Als Spitzenkandidat möchte ich mich im Stadtrat für eine traditionsreiche, familienfreundliche und lebendige Stadt einsetzen. Für mich stehen Verantwortung und Transparenz im Mittelpunkt politischen Handelns. Gemeinsam möchte ich mit Ihrem Vertrauen die Region stärken und mutig die Herausforderungen von morgen angehen. Veränderung beginnt nicht nur in Berlin, sondern auch hier bei uns. Lasst uns zusammen vor Ort etwas bewegen!"
    },
    {
      id: 2,
      name: "Monika Hartmann",
      position: "Verkäuferin, 39 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/Monika+Hartmann-1920w.jpg",
      description: "Ich bin in Bad Windsheim geboren und lebe - bis auf zwei Jahre - schon mein ganzes Leben hier. Ich bin verheiratet und habe zwei jugendliche Kinder. Unsere Stadt ist mein Zuhause. Ich möchte mit anpacken, Probleme offen ansprechen und dafür sorgen, dass unsere Stadt sich ehrlich, transparent und sinnvoll weiterentwickelt. Hier ist in den letzten Jahren viel Gutes entstanden, aber auch manches nicht so gelaufen. Darum möchte ich mich für Sie im Stadtrat engagieren."
    },
    {
      id: 3,
      name: "Dr. Benno Bulitta",
      position: "Dipl.-Volkswirt, 75 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/009+Dr.+Benno+Bulitta-1920w.jpeg",
      description: "Ich möchte mich unter anderem besonders für die Barrierefreiheit in Bad Windsheim und als ehemaliger Leiter des Kreisfremdenverkehrsamtes für die Förderung des Tourismus und der Gastronomie in unserer Stadt und in unserem Landkreis engagieren. Dabei darf ich auf 22 Jahre als Stadt-und Kreisrat zurückblicken und als 'Senior' meine Erfahrungen an die junge Generation weitergeben."
    },
    {
      id: 4,
      name: "Thomas Nölp",
      position: "Industriemechaniker, 33 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/Thomas+N%C3%B6lp+kleiner-1920w.jpg",
      description: "Als Familienvater werde ich mich im Stadtrat für die Wohnungsvergabe bei städtischen Immobilien mit preiswerten Mieten vor allem kinderreiche Familien und für preiswerte kleinere Wohnungen für Senioren einsetzen. Auch soll Sorge dafür getragen werden, dass kommunale Immobilien auch regelmäßig gepflegt werden, zum Beispiel bei Schimmel und undichten Dächern."
    },
    {
      id: 5,
      name: "Ramona Nölp",
      position: "Verkäuferin, 43 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/Ramona-1920w.jpg",
      description: "Für eine ausgewogene Familienpolitik und für Kitas, in denen die Kinder noch Kinder sein dürfen. Keine ideologisch geprägten Erziehungsmaßnahmen, sondern echte und liebevolle Zuwendung. Den Waldkindergarten finde ich Spitze, weil die Kinder dort frühzeitig lernen, mit der Natur umzugehen und sich für die Bewahrung der Umwelt einzusetzen."
    },
    {
      id: 6,
      name: "Silvio Grube",
      position: "Sicherheitsmitarbeiter, 46 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/Silvio+Bild-1920w.jpg",
      description: "Ich werde mich dafür einsetzen, dass unsere Straßen und Anlagen sicherer sind. Dazu gehören meines Erachtens auch eine Videoüberwachung und Aufzeichnungen, die bei einer Straftat verwendet werden können. Ich sehe darin eine Abschreckung, die dazu führen soll, dass diese Straftaten erst gar nicht vorkommen."
    },
    {
      id: 7,
      name: "Karen Wittich",
      position: "Sicherheitsmitarbeiterin, 34 Jahre",
      image: "https://le-cdn.website-editor.net/f4f5b2f6065c4ec2b536c829d952713f/dms3rep/multi/opt/Karen+Bild-1920w.jpg",
      description: "Ich muss immer an die Frauen denken, die in der Dunkelheit alleine unterwegs sein müssen. Deshalb ist für mich eine gute Straßenbeleuchtung notwendig. Dabei dürfen die Ortsteile auch nicht ausgeschlossen werden. Auch will ich mich dafür einsetzen, dass Frauen den Mut haben, Vorkommnisse zu melden. Dafür braucht es auch im Stadtrat Menschen mit Verständnis und Hilfsbereitschaft."
    },
    {
      id: 8,
      name: "Bennie Crawford",
      position: "Baufinanzierungsberater, 24 Jahre",
      image: "https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/c79e98efbee9b89c3d670c5b0fbf4a33.png",
      description: "Damit Bad Windsheim für junge Menschen eine Heimat mit Zukunft bleibt, brauchen wir attraktiven Wohnraum statt schleichendem Verfall. Als aktiver Baufinanzierungsberater mache ich mich dafür stark, dass Wohnraum für uns Bürger geschaffen und unsere städtische Identität geschützt wird. Weiterhin setze ich mich konsequent dafür ein, dass Sicherheit und der Erhalt unserer Heimat bei jeder Entscheidung Vorrang haben."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/f29ba073841dbacf8807be892165979d.png"
            alt="AfD Bad Windsheim"
            className="w-full h-full object-cover object-[center_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/60 via-primary-900/40 to-primary-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white py-20 mt-16">
          <div className="mb-6 inline-flex items-center gap-2 bg-secondary-600/90 px-4 py-2 rounded-full shadow-lg">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-sm font-semibold tracking-wider uppercase">Kommunalwahl 2026</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
            Gemeinsam für eine<br />starke Zukunft
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200 max-w-3xl mx-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
            Wir setzen uns für Bürgernähe, Transparenz und eine lebenswerte Stadt Bad Windsheim ein.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/kandidaten" 
              className="bg-secondary-600 hover:bg-secondary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-xl"
            >
              Unsere Kandidaten
            </Link>
            <Link 
              to="/programm" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-xl"
            >
              Unser Programm
            </Link>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">
            Herzlich Willkommen bei der AfD - Bad Windsheim
          </h2>
          <Link 
            to="/ueber-uns" 
            className="inline-flex items-center gap-2 bg-secondary-600 hover:bg-secondary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
          >
            Mehr über uns erfahren
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-6">
                <div className="w-12 h-1 bg-secondary-600"></div>
                <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Unsere Mission</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                Bürgernähe und Offenheit
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Ihre Meinung zählt! Wir möchten regelmäßig Ihre Anregungen und Ideen durch Befragungen einholen, um die Stadtentwicklung bürgernah zu gestalten.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-secondary-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Transparente Politik</h3>
                    <p className="text-gray-600">Offene Kommunikation und nachvollziehbare Entscheidungen</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-secondary-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Für alle Ortsteile</h3>
                    <p className="text-gray-600">Regelmäßige Begehungen mit Bürgerbeteiligung</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-secondary-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Barrierefreiheit</h3>
                    <p className="text-gray-600">Verbindliche Satzungen für Behinderte und Senioren</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-2xl opacity-20"></div>
              <img 
                src="https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/ad92e76f525a3092b6db20d0bc38e3ae.png"
                alt="AfD Bad Windsheim Logo"
                className="relative w-full rounded-2xl shadow-2xl border-4 border-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Seven Arguments Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Unsere Argumente</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Sieben Argumente, warum eine starke AfD gut für die Kommunalpolitik ist
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-primary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  1
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Ein großer Teil der Menschen fühlt sich durch die etablierten Parteien nicht mehr vertreten. Eine starke AfD in den Kommunalparlamenten stellt sicher, dass diese Wählerstimmen Gehör finden und deren Themen wie zum Beispiel eine kritische Haltung zur Migrationspolitik vor Ort oder die Kritik an Windkraftprojekten direkt in die Gremien getragen werden.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-secondary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-secondary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  2
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Eine starke AfD ist eine wichtige Kontrollinstanz gegenüber einer „festgefahrenen" Lokalpolitik. Durch gezielte Anfragen zu Haushaltsausgaben (z. B. Kosten für die Migration oder Beraterverträge) sorgt die AfD für mehr Transparenz und einen verantwortungsvollen Umgang mit Steuergeldern.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-primary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  3
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Eine starke AfD, zwingt die anderen Fraktionen dazu, sich inhaltlich intensiver mit Positionen auseinanderzusetzen, die sie zuvor vielleicht (aus welchen Gründen auch immer) nicht beachtet haben. Dies führt zu einer Belebung des politischen Wettbewerbs - zum Wohle unserer Bürger.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-secondary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-secondary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  4
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Eine starke AfD sorgt in den kommunalen Gremien dafür, das ideologische bundespolitische Themen keinen negativen Einfluss auf die Lösung lokaler Probleme haben und dass immer effiziente Lösungen gemeinsam gefunden werden müssen.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-primary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  5
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Eine starke AfD, zwingt die anderen Fraktionen dazu, sich um Mehrheiten zu bemühen. Damit wird die Zusammenarbeit gefördert, denn starre Mehrheiten sorgen (mit einem Fraktionszwang) oft dafür, dass einseitige Entscheidungen am Bürger vorbei gefällt werden.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-secondary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-secondary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  6
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Prinzipiell sorgt eine starke AfD dafür, dass Sachthemen vor Ideologie stehen. Auch wenn wir manchmal etwas emotional, laut sind und in der Wortwahl manchmal auch Tabus brechen (müssen), geht es uns in erster Linie immer um eine Stärkung der echten Demokratie durch eine direkte Bürgerbeteiligung und sinnvolle sowie finanzierbar Lösungen für die Menschen zu erreichen.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-primary-600 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-full flex-shrink-0 text-xl font-bold">
                  7
                </div>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Auch wenn die anderen Parteien vor dem Dilemma stehen, wie sie mit einer starken AfD umgehen sollen, werden wir beweisen, dass man mit unseren kommunalen Mandatsträgern immer reden und auch kontrovers diskutieren kann, dass wir zuhören können und dass wir bereit sind, andere Argumente in unsere Entscheidungen einzubeziehen. Uns geht es immer um die Sache und nicht um die Ideologie oder um Rechthaberei. Das ist unser Versprechen!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Candidates Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Unser Team</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Stadtratskandidaten 2026
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Lernen Sie unser engagiertes Team kennen. Fahren Sie mit der Maus über die Bilder, um mehr zu erfahren.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stadtratCandidates.map((candidate) => (
              <div
                key={candidate.id}
                className="relative group cursor-pointer overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-primary-300"
                onMouseEnter={() => setHoveredCandidate(candidate.id)}
                onMouseLeave={() => setHoveredCandidate(null)}
              >
                <div className="aspect-[3/4] relative">
                  <img
                    src={candidate.image}
                    alt={candidate.name}
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${candidate.id === 8 ? 'object-[center_20%] scale-125' : 'object-top'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-900/90 via-primary-900/50 to-transparent"></div>
                  
                  {/* Default Info */}
                  <div className={`absolute bottom-0 left-0 right-0 p-6 text-white transition-all duration-500 ${hoveredCandidate === candidate.id ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                    <div className="text-2xl font-bold mb-1 text-secondary-400">{candidate.id < 10 ? `0${candidate.id}` : candidate.id}</div>
                    <h3 className="text-xl font-bold mb-2">{candidate.name}</h3>
                    <p className="text-sm text-gray-300">{candidate.position}</p>
                  </div>

                  {/* Hover Info */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-secondary-600 to-secondary-700 p-6 flex flex-col justify-start overflow-y-auto transition-all duration-500 ${hoveredCandidate === candidate.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`}>
                    <div className="text-white">
                      <div className="text-3xl font-bold mb-3">{candidate.id < 10 ? `0${candidate.id}` : candidate.id}</div>
                      <h3 className="text-2xl font-bold mb-3">{candidate.name}</h3>
                      <p className="text-sm mb-4 font-semibold">{candidate.position}</p>
                      <p className="text-sm leading-relaxed">{candidate.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/kandidaten" 
              className="inline-flex items-center gap-2 bg-secondary-600 hover:bg-secondary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Alle Kandidaten ansehen
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Issues Section */}
      <section className="py-24 bg-primary-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-500"></div>
              <span className="text-secondary-400 font-semibold uppercase tracking-wider text-sm">Unsere Themen</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Wofür wir stehen
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-primary-800 p-8 rounded-xl hover:bg-primary-750 transition-colors duration-300 border-2 border-primary-700 hover:border-secondary-500">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-600 to-secondary-700 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Bezahlbares Wohnen</h3>
              <p className="text-primary-200 leading-relaxed">
                Unterstützung privater und städtischer Investitionen, Sanierung von Wohngebäuden, Familien mit Kindern bevorzugen bei Wohnungsvergabe.
              </p>
            </div>

            <div className="bg-primary-800 p-8 rounded-xl hover:bg-primary-750 transition-colors duration-300 border-2 border-primary-700 hover:border-secondary-500">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-600 to-secondary-700 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-police-car-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Sicherheit</h3>
              <p className="text-primary-200 leading-relaxed">
                Energiesparende Straßenbeleuchtung, mehr Sicherheitspräsenz durch Kameras, Verbesserung der Beleuchtung in dunklen Bereichen.
              </p>
            </div>

            <div className="bg-primary-800 p-8 rounded-xl hover:bg-primary-750 transition-colors duration-300 border-2 border-primary-700 hover:border-secondary-500">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-600 to-secondary-700 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Gesundheit</h3>
              <p className="text-primary-200 leading-relaxed">
                Starkes Krankenhaus: Wir setzen uns für den Erhalt und Ausbau unseres Krankenhauses als unverzichtbare Bestandteil der medizinischen Versorgung ein.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/programm" 
              className="inline-flex items-center gap-2 bg-secondary-600 hover:bg-secondary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Vollständiges Programm
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-secondary-600 to-secondary-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Unterstützen Sie uns bei der Wahl
          </h2>
          <p className="text-xl mb-8 text-red-100">
            Bitte kreuzen Sie einfach die <strong>Liste 3 - AfD</strong> oben an, dann erhält jeder Kandidat drei Stimmen und Sie haben Ihr volles Stimmen-Potenzial von 24 Stimmen ausgenutzt.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/stadtratswahl" 
              className="bg-white text-secondary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Infos zur Stadtratswahl
            </Link>
            <Link 
              to="/kreistagswahl" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Infos zur Kreistagswahl
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
